import { authClient } from '@/lib/auth-client';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { APP_NAME } from '@sharpe/shared/config';
import {
  Logout,
  Debug,
  Light,
  Asleep,
  WatsonHealthStackedMove,
  Wallet,
  Email,
  Enterprise,
  ChevronSort,
  Checkmark,
} from '@carbon/icons-react';
import { useTheme } from '@sharpe/ui/theme';
import { cn } from '@sharpe/ui/utils';
import { Badge, Popover, PopoverButton, PopoverPanel, Switch } from '@sharpe/ui/catalyst';
import { useCurrentUser, useOrgSwitcher } from '@/hooks/use-current-user-org';

import { UserDebugTrigger } from '@/components/auth/user-debug-overlay';
import { trpc } from '@/lib/trpc';
import { useRouter } from '@/hooks/use-routing';
import { formatCredits } from '@sharpe/shared/billing';

function formatRole(role: string): string {
  if (role === 'owner') return 'Owner';
  if (role === 'admin') return 'Admin';
  return 'Member';
}

const itemClasses =
  'group flex items-center gap-2.5 w-full rounded-sm px-2.5 py-1 text-left text-[13px]/5 text-foreground transition-colors cursor-pointer focus:outline-hidden hover:bg-accent hover:text-accent-foreground';

const iconClasses = 'size-4 shrink-0 text-muted-foreground group-hover:text-accent-foreground';

const dividerClasses = 'mx-3 my-1 h-px bg-border/60';

interface UserMenuProps {
  isCollapsed?: boolean;
}

export function UserMenu({ isCollapsed = false }: UserMenuProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    return (
      <div className="h-[50px] flex items-center px-3">
        <div className="h-7 w-7 rounded-sm bg-muted-foreground/10" />
      </div>
    );
  }

  return <UserMenuContent isCollapsed={isCollapsed} />;
}

function UserMenuContent({ isCollapsed }: UserMenuProps) {
  const {
    user,
    isLoading,
    isAuthenticated,
    isStackAdmin,
    adminViewEnabled,
    debugMode,
    setAdminViewEnabled,
    setDebugMode,
  } = useCurrentUser();
  const { theme, setTheme, resolvedTheme } = useTheme();

  const navigate = useNavigate();
  const router = useRouter();

  const { organizations, switchOrg, isSwitching } = useOrgSwitcher();

  const { data: balanceData } = trpc.usage.autumnBalance.useQuery(undefined, {
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  });
  const { data: productsData } = trpc.usage.getAvailableProducts.useQuery();
  const isAutumnEnabled = (productsData?.products?.length ?? 0) > 0;
  const credits = balanceData?.balance?.balance ?? 0;

  const { data: pendingInvitationsData } = trpc.org.invitations.myPending.useQuery(undefined, {
    refetchInterval: 60_000,
  });
  const pendingInvitationsCount = pendingInvitationsData?.invitations?.length ?? 0;

  if (isLoading) {
    return (
      <div className="h-[50px] flex items-center px-3">
        <div className="h-7 w-7 animate-pulse rounded-sm bg-muted-foreground/10" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return null;
  }

  const handleSignOut = async () => {
    await authClient.signOut();
    window.location.href = '/auth/signin';
  };

  const toggleTheme = () => {
    if (theme === 'system') {
      setTheme(resolvedTheme === 'dark' ? 'light' : 'dark');
    } else {
      setTheme(theme === 'dark' ? 'light' : 'dark');
    }
  };

  const getThemeLabel = () => {
    if (theme === 'system') {
      return `System (${resolvedTheme === 'dark' ? 'Dark' : 'Light'})`;
    }
    return theme === 'dark' ? 'Dark' : 'Light';
  };

  const handleOrgSwitch = async (orgId: string, close: () => void) => {
    if (orgId !== user.organizationId) {
      await switchOrg(orgId);
      router.refresh();
    }
    close();
  };

  const hasBillingContent = isAutumnEnabled || pendingInvitationsCount > 0;

  return (
    <Popover className="relative">
      <PopoverButton
        as="div"
        className="h-[50px] flex items-center gap-3 hover:bg-muted-foreground/5 cursor-pointer transition-colors px-3"
      >
        <div
          className="flex-shrink-0 flex items-center justify-center bg-muted-foreground rounded-[2px]"
          style={{ width: 28, height: 28 }}
        >
          <span className="text-sm font-medium text-background leading-none -translate-y-px">
            {user.name ? user.name.charAt(0).toUpperCase() : APP_NAME.toUpperCase().charAt(0)}
          </span>
        </div>
        {!isCollapsed && (
          <>
            <div className="flex flex-col min-w-0 flex-1">
              <span className="text-[13px] text-foreground truncate">{user.name}</span>
              <span className="text-label-md text-muted-foreground/60 truncate">
                {user.organizationName || user.email}
              </span>
            </div>
            <ChevronSort size={14} className="text-muted-foreground" />
          </>
        )}
      </PopoverButton>

      <PopoverPanel
        anchor="top start"
        className="w-[240px] bg-popover/75 backdrop-blur-xl rounded-md shadow-lg ring-1 ring-border dark:ring-inset overflow-hidden z-50 transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0"
      >
        {({ close }) => (
          <>
            {/* Email */}
            <div className="px-3.5 pt-2.5 pb-1">
              <span className="text-xs/4 text-muted-foreground truncate block">{user.email}</span>
            </div>

            {/* Organizations */}
            {organizations.length > 0 && (
              <div className="p-1">
                {organizations.map((org) => {
                  const isCurrentOrg = org.organizationId === user.organizationId;
                  return (
                    <button
                      key={org.organizationId}
                      onClick={() => handleOrgSwitch(org.organizationId, close)}
                      disabled={isSwitching}
                      className={cn(
                        'group flex items-center gap-2.5 w-full rounded-sm px-2.5 py-1.5 text-left transition-colors cursor-pointer focus:outline-hidden hover:bg-accent hover:text-accent-foreground',
                        isSwitching && 'opacity-50 cursor-not-allowed'
                      )}
                    >
                      <div className="size-7 shrink-0 rounded-sm bg-muted-foreground/10 flex items-center justify-center">
                        <Enterprise
                          size={14}
                          className="text-muted-foreground group-hover:text-accent-foreground"
                        />
                      </div>
                      <div className="flex flex-col gap-1 min-w-0 flex-1">
                        <span className="text-[13px]/4 text-foreground truncate">
                          Your organization
                        </span>
                        <div className="flex items-center gap-1">
                          <Badge compact color="zinc">
                            {org.planName || 'Free'} plan
                          </Badge>
                          <Badge compact color="zinc">
                            {formatRole(org.role)}
                          </Badge>
                        </div>
                      </div>
                      {isCurrentOrg && <Checkmark size={14} className="shrink-0 text-foreground" />}
                    </button>
                  );
                })}
              </div>
            )}

            <div className={dividerClasses} />

            {/* Settings */}
            <div className="p-1">
              <button onClick={toggleTheme} className={itemClasses}>
                {resolvedTheme === 'dark' ? (
                  <Light size={16} className={iconClasses} />
                ) : (
                  <Asleep size={16} className={iconClasses} />
                )}
                <span>{getThemeLabel()}</span>
              </button>

              {isStackAdmin && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setAdminViewEnabled(!adminViewEnabled)}
                  onKeyDown={(e) => e.key === 'Enter' && setAdminViewEnabled(!adminViewEnabled)}
                  className={cn(itemClasses, 'justify-between')}
                >
                  <div className="flex items-center gap-2.5">
                    <WatsonHealthStackedMove size={16} className={iconClasses} />
                    <span>Stack Admin</span>
                  </div>
                  <span onClick={(e) => e.stopPropagation()}>
                    <Switch checked={adminViewEnabled} onChange={setAdminViewEnabled} />
                  </span>
                </div>
              )}

              {isStackAdmin && (
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => setDebugMode(!debugMode)}
                  onKeyDown={(e) => e.key === 'Enter' && setDebugMode(!debugMode)}
                  className={cn(itemClasses, 'justify-between')}
                >
                  <div className="flex items-center gap-2.5">
                    <Debug size={16} className={iconClasses} />
                    <span>Debug mode</span>
                  </div>
                  <span onClick={(e) => e.stopPropagation()}>
                    <Switch checked={debugMode} onChange={setDebugMode} />
                  </span>
                </div>
              )}

              {isStackAdmin && debugMode && <UserDebugTrigger />}
            </div>

            {/* Billing & invitations */}
            {hasBillingContent && (
              <>
                <div className={dividerClasses} />
                <div className="p-1">
                  {isAutumnEnabled && (
                    <div className="flex items-center gap-3 px-3 py-1.5 text-sm/6 text-muted-foreground">
                      <Wallet size={16} className="size-4 shrink-0" />
                      <span className="tabular-nums">{formatCredits(credits)} credits</span>
                    </div>
                  )}

                  {pendingInvitationsCount > 0 && (
                    <button
                      onClick={() => {
                        const firstInvite = pendingInvitationsData?.invitations?.[0];
                        if (firstInvite) {
                          navigate(`/invite/accept?token=${firstInvite.token}`);
                        }
                        close();
                      }}
                      className={itemClasses}
                    >
                      <span className="relative shrink-0">
                        <Email size={16} className="text-ds-amber-900" />
                        <span className="absolute -top-1.5 -right-1.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-destructive px-0.5 text-[9px] font-medium text-white leading-none">
                          {pendingInvitationsCount}
                        </span>
                      </span>
                      <span>
                        {pendingInvitationsCount} pending{' '}
                        {pendingInvitationsCount === 1 ? 'invitation' : 'invitations'}
                      </span>
                    </button>
                  )}
                </div>
              </>
            )}

            <div className={dividerClasses} />

            {/* Sign out */}
            <div className="p-1">
              <button
                onClick={handleSignOut}
                className={cn(
                  itemClasses,
                  'text-muted-foreground hover:text-ds-red-600 hover:bg-ds-red-200/50'
                )}
              >
                <Logout size={16} className="size-4 shrink-0" />
                <span>Sign out</span>
              </button>
            </div>
          </>
        )}
      </PopoverPanel>
    </Popover>
  );
}
